import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LANG, formatHour, LANG_KEY, LANGS, langFromCookie, LOCALES, localizeDemo, parseLang, type Lang } from '@pizza-roma/shared';
import { en, type Messages } from './en';
import { fr } from './fr';

const DICTS: Record<Lang, Messages> = { en, fr };

/**
 * Language choice: `?lang=en|fr` in the URL, else localStorage `nx-lang`, else the `nx-lang` cookie,
 * else English. The choice is saved in localStorage and in a cookie (the API reads the cookie to
 * answer error messages and the CSV export in the same language).
 */
function detect(): Lang {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const fromUrl = parseLang(new URLSearchParams(window.location.search).get('lang'));
  let stored: Lang | null = null;
  try {
    stored = parseLang(localStorage.getItem(LANG_KEY));
  } catch {
    /* storage unavailable */
  }
  return fromUrl ?? stored ?? langFromCookie(document.cookie) ?? DEFAULT_LANG;
}

function persist(lang: Lang) {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* storage unavailable */
  }
  document.cookie = `${LANG_KEY}=${lang}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

/** <html lang>, meta description and the PWA manifest follow the language. */
function applyToDocument(lang: Lang) {
  const root = document.documentElement;
  root.lang = LOCALES[lang];
  const t = DICTS[lang];
  document.querySelector('meta[name="description"]')?.setAttribute('content', t.meta.description);
  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest) {
    const driver = manifest.getAttribute('href')?.includes('manifest-driver');
    manifest.setAttribute('href', `/manifest-${driver ? 'driver' : 'client'}${lang === 'fr' ? '.fr' : ''}.webmanifest`);
  }
  const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appTitle && location.pathname.startsWith('/driver')) appTitle.setAttribute('content', t.meta.driverAppName);
}

let current: Lang = DEFAULT_LANG;
if (typeof window !== 'undefined') {
  current = detect();
  persist(current);
  applyToDocument(current);
}

/** Current language for code outside React components (formatting, API errors). */
export const getLang = () => current;
export const getLocale = () => LOCALES[current];
export const getMessages = () => DICTS[current];

export interface I18n {
  lang: Lang;
  locale: string;
  t: Messages;
  setLang: (l: Lang) => void;
  /** Demo data (menu, options, promo…) in the current language; other text unchanged. */
  demo: (text: string | null | undefined) => string;
}

const Ctx = createContext<I18n>({
  lang: current,
  locale: LOCALES[current],
  t: DICTS[current],
  setLang: () => {},
  demo: (s) => localizeDemo(s ?? '', current),
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(current);
  const setLang = useCallback((l: Lang) => {
    current = l;
    persist(l);
    applyToDocument(l);
    // Keep a shared ?lang= link consistent with the new choice.
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
      url.searchParams.set('lang', l);
      window.history.replaceState(window.history.state, '', url);
    }
    setLangState(l);
  }, []);
  const value = useMemo<I18n>(
    () => ({ lang, locale: LOCALES[lang], t: DICTS[lang], setLang, demo: (s) => localizeDemo(s ?? '', lang) }),
    [lang, setLang],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);

/** Small FR / EN switch shown in every shell. */
export function LangToggle({ dark, className = '' }: { dark?: boolean; className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className={`lang-toggle ${dark ? 'dark' : ''} ${className}`} role="group" aria-label={t.common.language} data-testid="lang-toggle">
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          lang={LOCALES[l]}
          className={lang === l ? 'active' : ''}
          aria-pressed={lang === l}
          aria-label={t.common.langNames[l]}
          title={t.common.langNames[l]}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** "11:00 – 23:00" (fr, as before) or "11:00 a.m. – 11:00 p.m." (en). */
export function hoursRange(open: string, close: string, lang: Lang) {
  return lang === 'fr' ? `${open} – ${close}` : `${formatHour(open, lang)} – ${formatHour(close, lang)}`;
}

/** Today's opening hours from the API's open status (opensAt / closesAt). */
export function todayHours(s: { opensAt?: string; closesAt?: string }, lang: Lang, t: Messages) {
  return s.opensAt && s.closesAt ? `${formatHour(s.opensAt, lang)} – ${formatHour(s.closesAt, lang)}` : t.common.closedToday;
}
